import Image from 'next/image';
import { Section, Container, SectionHeader } from '@layout';
import { Breadcrumb, Button, MainHero, type BreadcrumbItem } from '@ui';
import { portfolioUxCaseStudyHeroTitle } from '@lib/portfolioUxCaseStudyTitle';
import { sectionSlug, type PortfolioImage, type PortfolioSection } from '@lib/portfolioSections';
import { t } from '@utils/translate';
import { renderTranslation } from '@utils/renderTranslation';

export interface PortfolioItemDetailProps {
  locale: 'en' | 'es';
  section: PortfolioSection;
  image: PortfolioImage;
  breadcrumbItems: BreadcrumbItem[];
  breadcrumbAriaLabel: string;
}

const DEFAULT_IMAGE_URL = '/images/app-icons/logo.svg';

export default function PortfolioItemDetail({
  locale,
  section,
  image,
  breadcrumbItems,
  breadcrumbAriaLabel,
}: PortfolioItemDetailProps) {
  const sec = sectionSlug(section);
  const descriptionText =
    (image.description_long?.trim() || image.description?.trim() || '').trim() || '';
  const ux = image.uxCaseStudy;
  const heroTitle = portfolioUxCaseStudyHeroTitle(image, locale);
  const heroDescriptionText = ux
    ? ux.sectionDescription?.trim() || descriptionText
    : descriptionText;
  const rawSrc = (image.image_url ?? '').trim() || DEFAULT_IMAGE_URL;
  const isDefaultImage = !(image.image_url ?? '').trim();
  const href = image.url?.trim();
  const target = image.target?.trim();
  const rel = image.rel?.trim() || (target === '_blank' ? 'noreferrer' : undefined);
  const visitSiteLabel = String(
    renderTranslation(t('actions.view_site', 'global', locale)) || 'Visit site'
  );

  return (
    <>
      <MainHero
        className='portfolio-hero'
        main={
          <>
            <Breadcrumb ariaLabel={breadcrumbAriaLabel} items={breadcrumbItems} />
            <SectionHeader title={heroTitle} description='' as='h1' />
            {heroDescriptionText
              ? heroDescriptionText.split(/\n\n+/).map((para, i) => (
                  <p className='sub' key={i}>
                    {para}
                  </p>
                ))
              : null}
          </>
        }
      />
      <div className='section-group'>
        <Section sectionID={`portfolio-item-${sec}-${image.id}`} className='main-section portfolio'>
          <Container>
            <div className='case-study-container'>
              {/* <Heading headingText={ux?.sectionTitle} headingType='h2' />
            <p className='case-study-description'>{ux?.sectionDescription}</p> */}
              <div className='case-study-blocks'>
                {ux?.blocks?.map((block, index) => (
                  <div key={index} className='case-study-block'>
                    <SectionHeader title={block.title} description='' as='h3' />
                    <p className='case-study-block-body'>{block.body}</p>
                  </div>
                ))}
              </div>
            </div>
            <div
              className={['portfolio-item-media', isDefaultImage ? 'is-default-image' : '']
                .filter(Boolean)
                .join(' ')}
            >
              <Image
                src={rawSrc}
                alt={image.title}
                width={1200}
                height={900}
                style={{
                  width: '100%',
                  height: 'auto',
                  objectFit: isDefaultImage ? 'contain' : 'cover',
                }}
                unoptimized={rawSrc.toLowerCase().endsWith('.svg')}
              />
            </div>
            {href ? (
              <p className='portfolio-item-actions'>
                <Button
                  as='a'
                  href={href}
                  target={target || undefined}
                  rel={rel}
                  className='btn-primary'
                  label={visitSiteLabel}
                />
              </p>
            ) : null}
          </Container>
        </Section>
      </div>
    </>
  );
}
